import { RootMessenger } from './communication/root'
import { type IBrokerContext } from './context'
import { type IBrokerPluginContext, type IBrokerPlugin } from './broker/plugin'
import { BrokerPluginLoader, type BrokerPluginInstallOptionType } from './broker'
import { ClientProviderPluginLoader, type ClientProviderPluginInstallOptionType } from './client-provider'
import { IFrameClientProviderPluginInstaller } from './client-provider/iframe/plugin'
import { ScriptClientProviderPluginInstaller } from './client-provider/script/plugin-url'
import { LocalClientProviderPluginInstaller } from './client-provider/script/plugin-local'
import { ScriptBrokerPluginInstaller } from './broker/installers/script'
import { LocalBrokerPluginInstaller } from './broker/installers/local'
import { DefaultApiManager } from './communication/apis'
import { Logger } from './utils/logger'
import { type LocalEventEmitter } from './communication/local'

export interface AppSystemBrokerInitConfig {
  broker?: {
    plugins?: BrokerPluginInstallOptionType[]
    /**
     * List of allowed URL origins to be imported by broker plugin loader.
     * Wildcard can be used.
     * Same origin is allowed by default.
     */
    scriptPluginAllowedOrigins?: string[]
  }
  client?: {
    providerPlugins?: ClientProviderPluginInstallOptionType[]
    /**
    * List of allowed URL origins to be imported by client provider loader.
    * Wildcard can be used.
    * Same origin is allowed by default.
    */
    scriptPluginAllowedOrigins?: string[]
  }
  logger?(): void
}

class AppSystemBroker {
  ctx: IBrokerContext
  brokerPluginLoader: BrokerPluginLoader
  clientProviderPluginLoader: ClientProviderPluginLoader
  initConfig: AppSystemBrokerInitConfig

  constructor (initConfig: AppSystemBrokerInitConfig) {
    this.initConfig = initConfig
    const rootMessenger = new RootMessenger()
    this.ctx = {
      logger: new Logger(this.initConfig.logger ?? (() => {})),
      rootMessenger,
      eventEmitter: rootMessenger.createLocalEmitter('broker'),
      apis: new DefaultApiManager(rootMessenger),
      initConfig
    }
    this.brokerPluginLoader = new BrokerPluginLoader(this.ctx)
    this.brokerPluginLoader
      .addInstaller('script', new ScriptBrokerPluginInstaller())
      .addInstaller('local', new LocalBrokerPluginInstaller())

    this.clientProviderPluginLoader = new ClientProviderPluginLoader()
    this.clientProviderPluginLoader
      .addInstaller('iframe', new IFrameClientProviderPluginInstaller(this.ctx))
      .addInstaller('script', new ScriptClientProviderPluginInstaller(this.ctx))
      .addInstaller('local', new LocalClientProviderPluginInstaller(this.ctx))
  }

  async initialize () {
    for (const plugin of this.initConfig.broker?.plugins ?? []) {
      await this.loadBrokerPlugin(plugin)
    }
    for (const plugin of this.initConfig.client?.providerPlugins ?? []) {
      await this.loadClientProviderPlugin(plugin)
    }
  }

  async loadBrokerPlugin (plugin: BrokerPluginInstallOptionType) {
    await this.brokerPluginLoader.load(plugin)
  }

  async loadClientProviderPlugin (plugin: ClientProviderPluginInstallOptionType) {
    await this.clientProviderPluginLoader.load(plugin)
  }

  emit (...args: Parameters<LocalEventEmitter['emit']>) {
    this.ctx.eventEmitter.emit(...args)
  }

  on (...args: Parameters<LocalEventEmitter['on']>) {
    this.ctx.eventEmitter.on(...args)
  }

  off (...args: Parameters<LocalEventEmitter['off']>) {
    this.ctx.eventEmitter.off(...args)
  }

  once (...args: Parameters<LocalEventEmitter['once']>) {
    this.ctx.eventEmitter.once(...args)
  }

  async connectClientApi<T>(pluginName: string, apiName: string): Promise<{ api: T }> {
    // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
    const found = this.clientProviderPluginLoader.loadedPlugins.find(p => p.name === pluginName)
    if (found == null) throw new Error(`The plugin is not found. name=${pluginName}`)
    const apiModule = await this.ctx.rootMessenger.connectClientApi<T>(found.id, apiName)
    if (apiModule == null) throw new Error(`The api ${apiName} is unknown or not provided by plugin: ${pluginName}`)
    return {
      api: apiModule
    }
  }
}

export { AppSystemBroker }
export type { AppSystemBrokerInitConfig as AppSystemBrokerConfig, IBrokerContext, IBrokerPlugin, IBrokerPluginContext, ClientProviderPluginInstallOptionType, BrokerPluginInstallOptionType }
